import { redirect } from "@sveltejs/kit";

import { getUnversionedRootRedirect } from "./reference-surfaces";

export type DocsRedirect = {
  /**
   * 301 for permanent redirects, 302 for temporary redirects.
   * NOTE: Do not use 308: it breaks urllib before Python 3.11,
   * which is still used by older Modal clients.
   */
  status: 301 | 302;
  /** Target pathname. Has a `.md` suffix if the source did. */
  pathname: string;
  /** `#fragment` from the rule, or empty. Always empty for `.md` targets. */
  hash: string;
};

type RuleTarget = {
  status?: 301 | 302;
  /** Target path, optionally with a `#fragment`. */
  location: string;
  /** False if the target has no `.md` sibling. */
  markdown?: boolean;
};

/** Receives the pathname with any trailing slash and `.md` suffix removed. */
type Rule = (path: string) => RuleTarget | null;

const LLMS_TXT_FILES = ["llms.txt", "llms-full.txt"];

// `/docs/reference/**` moved under `/docs/sdk/py/latest` and `/docs/cli/latest`.
const legacyReference: Rule = (path) => {
  if (path === "/docs/reference") {
    return { location: "/docs/sdk/py/latest" };
  }
  if (path === "/docs/reference/changelog") {
    return { location: "/docs/sdk/py/releases" };
  }
  if (
    path === "/docs/reference/cli" ||
    path.startsWith("/docs/reference/cli/")
  ) {
    return {
      location: path.replace("/docs/reference/cli", "/docs/cli/latest"),
    };
  }
  if (path.startsWith("/docs/reference/")) {
    return {
      location: path.replace("/docs/reference/", "/docs/sdk/py/latest/"),
    };
  }
  return null;
};

// The CLI ships in the Python package, so its release notes are the Python
// SDK's. `modal changelog` fetches `/docs/sdk/py/releases.md`.
const changelogAliases: Rule = (path) =>
  [
    "/docs/sdk/py/changelog",
    "/docs/cli/changelog",
    "/docs/cli/releases",
  ].includes(path)
    ? { location: "/docs/sdk/py/releases" }
    : null;

// Unversioned roots, e.g. `/docs/sdk/py` → `/docs/sdk/py/latest`.
const surfaceRoots: Rule = (path) => {
  const location = getUnversionedRootRedirect(path);
  return location ? { location } : null;
};

// Old path under `/docs/` → new path. An entry makes the old path unreachable,
// so rename the page in the same change.
export const RENAMED_PAGES: Record<string, string> = {
  cursor: "examples/cursor",
  "examples/a1111_webui": "examples/text_to_image",
  "examples/anthropic_computer_use": "examples/computer_use_vnc",
  "examples/boltz1": "examples/boltz_predict",
  "examples/cloud_bucket_mount_loras": "examples/s3_bucket_mount",
  "examples/cls_with_options": "guide/dynamic-function-config",
  "examples/code_interpreter": "examples/simple_code_interpreter",
  "examples/comfyapp": "examples/text_to_image",
  "examples/covid_datasette": "examples/cron_datasette",
  "examples/discord-musicgen": "examples/generate_music",
  "examples/dreambooth_app": "examples/diffusers_lora_finetune",
  "examples/flan_t5_finetune": "examples/llm-finetuning",
  "examples/grpo_trl": "examples/paint_flowers",
  "examples/grpo_verl": "examples/paint_flowers",
  "examples/http_server": "examples/server",
  "examples/http_server_sticky": "examples/server_affinity",
  "examples/llama_cpp": "examples/very_large_models",
  "examples/llm_inference": "examples/vllm_inference",
  "examples/misc-ml-examples": "examples/miscellaneous",
  "examples/musicgen": "examples/generate_music",
  "examples/parakeet": "examples/streaming_parakeet",
  "examples/playground": "examples/stable_diffusion_cli",
  "examples/run_sam": "examples/segment_anything",
  "examples/seamless-chat": "examples/llm-voice-chat",
  "examples/server_sticky": "examples/server_affinity",
  "examples/sgl_vlm": "examples/sglang_vlm",
  "examples/sglang_kitchen_sink": "examples/sglang_low_latency",
  "examples/slack-finetune": "examples/llm-finetuning",
  "examples/stable_diffusion_cli": "examples/text_to_image",
  "examples/stable_diffusion_xl": "examples/stable_diffusion_cli",
  "examples/stable_diffusion_xl_lightning": "examples/stable_diffusion_cli",
  "examples/stable_diffusion_xl_turbo": "examples/image_to_image",
  "examples/text_embeddings_inference": "examples/liquidai_embeddings_server",
  "examples/text_generation_inference": "examples/llm_inference",
  "examples/tgi_mixtral": "examples/llm_inference",
  "examples/trtllm_llama": "examples/trtllm_latency",
  "examples/vision_model_training": "examples/hp_sweep_gpt",
  "examples/vllm_gemma": "examples/llm_inference",
  "examples/vllm_mixtral": "examples/llm_inference",
  "examples/vllm_snapshot": "examples/ministral3_inference",
  "examples/web-scraper": "examples/webscraper",
  "examples/webcam": "examples/webrtc_yolo",
  "examples/whisper-transcriber": "examples/batched_whisper",
  "guide/custom-container": "guide/images",
  "guide/developing-with-llms": "cli/latest/skills",
  "guide/dicts-and-queues": "examples/dicts_and_queues",
  "guide/docker-in-sandboxes": "guide/sandboxes#running-docker-in-a-sandbox",
  "guide/dojo": "guide/training-quickstart",
  "guide/endpoint-benchmarks": "guide/dedicated-endpoints#benchmarks",
  "guide/endpoint-metrics": "guide/dedicated-endpoints#metrics",
  "guide/geographic-latency": "guide/region-selection",
  "guide/job-queues": "guide/job-queue",
  "guide/lifecycle": "guide/lifecycle-functions",
  "guide/lifecycle-methods": "guide/lifecycle-functions",
  "guide/memory-snapshot": "guide/memory-snapshots",
  "guide/mounting": "guide/project-structure",
  "guide/notebooks-modal": "guide/notebooks",
  "guide/parameterized-functions": "guide/parametrized-functions",
  "guide/private-registries": "guide/existing-images",
  "guide/s3-gateway-endpoints": "guide/region-selection",
  "guide/sandbox": "guide/sandboxes",
  "guide/sandbox-custom-domains": "guide/sandbox-networking#custom-domains",
  "guide/sandbox-directory-snapshots":
    "guide/sandbox-snapshots#directory-snapshots",
  "guide/sandbox-memory-snapshots": "guide/sandbox-snapshots#memory-snapshots",
  "guide/shared-volumes": "guide/network-file-systems",
  "guide/modal-user-account-setup": "guide/getting-started",
  "guide/vm-memory-snapshots": "guide/sandbox-snapshots#memory-snapshots",
  "guide/vm-sandboxes": "guide/sandboxes#runtimes",
  "guide/volumes-v2": "guide/volumes",
  "guide/web-endpoints": "guide/webhooks",
  "sdk/py/latest/modal.call_graph": "sdk/py/latest/types",
  "sdk/py/latest/modal.gpu": "guide/gpu",
  "sdk/py/latest/modal.web_endpoint": "sdk/py/latest/fastapi_endpoint",
  "sdk/py/latest/modal.Mount": "sdk/py/latest/Image",
  "sdk/py/latest/modal.Stub": "sdk/py/latest/App",
};

const renamedPages: Rule = (path) => {
  const name = path.slice("/docs/".length);
  // `hasOwn` so names like `constructor` do not match prototype keys.
  const renamed = Object.hasOwn(RENAMED_PAGES, name)
    ? RENAMED_PAGES[name]
    : undefined;
  return renamed ? { location: `/docs/${renamed}` } : null;
};

const legacyExamples: Rule = (path) => {
  const slug = path.slice("/docs/guide/ex/".length);
  return path.startsWith("/docs/guide/ex/") && slug
    ? { location: `/docs/examples/${slug}` }
    : null;
};

// Every `llms.txt` under `/docs` serves the root file. 302 in case a
// section-specific file is added later.
const llmsTxt: Rule = (path) => {
  const file = path.split("/").pop() ?? "";
  return LLMS_TXT_FILES.includes(file)
    ? { status: 302, location: `/${file}`, markdown: false }
    : null;
};

/** First match wins. */
const rules: Rule[] = [
  legacyReference,
  changelogAliases,
  surfaceRoots,
  renamedPages,
  legacyExamples,
  llmsTxt,
];

/**
 * Pure and synchronous so the same table runs in `handleDocsRedirects` on the
 * server and in the docs layout `load` on client-side navigation. Strips a
 * trailing slash and `.md` before matching, so rules are written for the HTML
 * path and cover the `.md` sibling too.
 */
export function resolveDocsRedirect(pathname: string): DocsRedirect | null {
  if (!pathname.startsWith("/docs/")) {
    return null;
  }
  let path = pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
  const markdown = path.endsWith(".md");
  if (markdown) {
    path = path.slice(0, -".md".length);
  }
  for (const rule of rules) {
    const target = rule(path);
    if (!target) {
      continue;
    }
    const hashIndex = target.location.indexOf("#");
    const location =
      hashIndex === -1 ? target.location : target.location.slice(0, hashIndex);
    const hash = hashIndex === -1 ? "" : target.location.slice(hashIndex);
    if (markdown && target.markdown !== false) {
      return {
        status: target.status ?? 301,
        pathname: `${location}.md`,
        hash: "",
      };
    }
    return { status: target.status ?? 301, pathname: location, hash };
  }
  return null;
}

/**
 * Redirects a `modal.<symbol>` Python reference page to `<symbol>` when that
 * page exists. Depends on content, so it is called from the Python reference
 * route rather than the rule table. `exists` receives the target pathname
 * without `.md`, e.g. `/docs/sdk/py/latest/App`, so the check and the redirect
 * name the same page even when the id has directory segments.
 */
export async function resolveModalPrefixRedirect(
  pathname: string,
  exists: (targetPathname: string) => boolean | Promise<boolean>,
): Promise<DocsRedirect | null> {
  const markdown = pathname.endsWith(".md");
  const path = markdown ? pathname.slice(0, -".md".length) : pathname;
  const slashIndex = path.lastIndexOf("/");
  const segment = path.slice(slashIndex + 1);
  if (!segment.startsWith("modal.")) {
    return null;
  }
  const target = `${path.slice(0, slashIndex + 1)}${segment.slice("modal.".length)}`;
  if (!(await exists(target))) {
    return null;
  }
  return {
    status: 301,
    pathname: markdown ? `${target}.md` : target,
    hash: "",
  };
}

/**
 * Throws the SvelteKit redirect, with `search` ahead of the fragment. The
 * rule's fragment wins; `hash` is the source URL's own fragment, which only
 * client-side navigation can see.
 */
export function redirectToDocs(
  target: DocsRedirect,
  search = "",
  hash = "",
): never {
  redirect(target.status, `${target.pathname}${search}${target.hash || hash}`);
}

/**
 * Returns the decode result rather than discarding it: the production bundler
 * treats `decodeURI` as side-effect free and drops a bare call, which turned
 * this check into `return true`.
 */
export function isDecodablePathname(pathname: string): boolean {
  try {
    return decodeURI(pathname) !== undefined;
  } catch {
    return false;
  }
}
